/**
 * Persist a pending call-check login across page reloads / mobile app backgrounding.
 *
 * A user has to leave the app to make the call; iOS/Android may reload or kill the
 * WebView. Without this, `checkId` lives only in React state, polling never resumes,
 * and the user is dropped back to the phone input screen ("nothing happens").
 */
export type PendingCallSurface = 'client' | 'trainer';

export type PendingCall = {
  checkId: string;
  /** Номер, на который нужно позвонить (номер оператора). */
  callPhonePretty: string;
  /** Телефон пользователя, с которого ждём звонок. */
  userPhone: string;
  orgSlug: string | null;
  surface: PendingCallSurface;
  createdAt: number;
};

const TTL_MS = 25 * 60 * 1000; // согласовано с authLoginStateTTL
const key = (surface: PendingCallSurface) => `domcrm_embed_pending_call_${surface}`;

export function savePendingCall(p: PendingCall): void {
  try {
    localStorage.setItem(key(p.surface), JSON.stringify(p));
  } catch {
    /* storage may be unavailable (private mode) — login still works in-page */
  }
}

export function clearPendingCall(surface: PendingCallSurface): void {
  try {
    localStorage.removeItem(key(surface));
  } catch {
    /* ignore */
  }
}

export function loadPendingCall(
  surface: PendingCallSurface,
  orgSlug: string | null
): PendingCall | null {
  try {
    const raw = localStorage.getItem(key(surface));
    if (!raw) return null;
    const p = JSON.parse(raw) as PendingCall;
    const fresh = typeof p.createdAt === 'number' && Date.now() - p.createdAt < TTL_MS;
    const sameOrg =
      !orgSlug || !p.orgSlug || p.orgSlug.trim().toLowerCase() === orgSlug.trim().toLowerCase();
    if (!fresh || !sameOrg || !p.checkId) {
      clearPendingCall(surface);
      return null;
    }
    return p;
  } catch {
    clearPendingCall(surface);
    return null;
  }
}
